import { useTranslation } from "react-i18next";

import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { CalendarChangeParams } from "primereact/calendar";

import { FormContainer } from "../../shared/FormLayout/FormContainer";
import { Card } from "../../shared/Card";
import { FormRow } from "../../shared/FormLayout/FormRow";

import { WorkOrderWithAssignment } from "../../shared/interfaces/WorkOrder";

import { StatusTag } from "../../StatusTag/StatusTag";

import { InternationalDatepicker } from "../../shared/InternationalDatepicker";
import { Statuses } from "../../shared/Enums";
import { getStartOfDay, getEndOfDay, getDateObject } from "../../shared/DateConversions";

interface WorkorderDetailsProps {
  workorder: WorkOrderWithAssignment;
  isLoading: boolean;
  editingWorkorder: boolean;
  onFieldChange: <KeyType extends keyof WorkOrderWithAssignment>(
    fieldName: KeyType,
    value: WorkOrderWithAssignment[KeyType] | undefined,
  ) => void;
}

export default function WorkorderDetails(props: WorkorderDetailsProps) {
  const { t } = useTranslation();
  const { workorder, isLoading, editingWorkorder, onFieldChange } = props;

  function handleTargetStartDateChange(e: CalendarChangeParams | undefined) {
    const targetStartDate = e?.value ? getStartOfDay(e?.value as Date) : undefined;
    onFieldChange("targetStartDate", targetStartDate);

    let targetCompletionDate = workorder.targetCompletionDate;

    if (!targetCompletionDate && targetStartDate) {
      targetCompletionDate = targetStartDate;
      onFieldChange("targetCompletionDate", targetCompletionDate);
    }
  }

  function handleTargetCompletionDateChange(e: CalendarChangeParams | undefined) {
    const targetCompletionDate = e?.value ? getEndOfDay(e?.value as Date) : undefined;
    onFieldChange("targetCompletionDate", targetCompletionDate);

    let targetStartDate = workorder.targetCompletionDate;

    if (!targetStartDate && targetCompletionDate) {
      targetStartDate = targetCompletionDate;
      onFieldChange("targetStartDate", targetStartDate);
    }
  }

  const onInputChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
    const key = event.target.name as keyof WorkOrderWithAssignment;
    const value = event.target.value;

    onFieldChange(key, value);
  };

  return (
    <Card title={t("workorders.workorderDetails")} className="mb-3" loading={isLoading}>
      <FormContainer>
        <FormRow label={t("customer.customer")} value={props.workorder?.customerName} />
        <FormRow
          label={t("customer.clientRef")}
          value={
            editingWorkorder ? (
              <InputText
                key={`input-clientId`}
                name="clientId"
                value={workorder.clientId?.toString() || ""}
                onChange={onInputChange}
                className="w-100"
                maxLength={50}
              />
            ) : (
              workorder?.clientId
            )
          }
        />
        <FormRow
          label={t("common.projectCode")}
          value={
            editingWorkorder ? (
              <InputText
                key={`input-projectCode`}
                name="projectCode"
                value={workorder.projectCode?.toString() || ""}
                onChange={onInputChange}
                className="w-100"
                maxLength={50}
              />
            ) : (
              workorder?.projectCode
            )
          }
        />
        <FormRow
          label={t("common.createdDate")}
          value={t("common.formattedDate", { dateString: workorder?.created })}
        />
        <FormRow label={t("common.createdBy")} value={workorder && workorder.createdByUserName} />
        <FormRow
          label={t("common.summary")}
          value={
            editingWorkorder ? (
              <InputText
                key={`input-description`}
                name="description"
                value={workorder.description?.toString() || ""}
                onChange={onInputChange}
                className="w-100"
                maxLength={200}
              />
            ) : (
              workorder?.description
            )
          }
        />
        <FormRow
          label={t("common.detailedDescription")}
          value={
            editingWorkorder ? (
              <InputTextarea
                key={`input-longDescription`}
                name="longDescription"
                value={workorder.longDescription?.toString() || ""}
                onChange={onInputChange}
                className="w-100"
              />
            ) : (
              workorder?.longDescription
            )
          }
        />
        <FormRow label={t("workorders.notes")} value={workorder?.notes} />
        <FormRow
          label={t("common.status")}
          value={
            workorder?.status && (
              <StatusTag
                status={workorder?.status}
                label={t(`workorders.statuses.${workorder?.status.toLowerCase()}`)}
              />
            )
          }
        />
        <FormRow
          label={t("workorders.statusDate")}
          value={t("common.formattedDate", {
            dateString: workorder?.statusDate,
          })}
        />
        {workorder.status === Statuses.COMPLETE && (
          <FormRow label={t("workorders.completedBy")} value={workorder?.completedByEmail} />
        )}
        <FormRow
          label={t("workorders.targetStartDate")}
          value={
            editingWorkorder ? (
              <InternationalDatepicker
                value={getDateObject(workorder.targetStartDate)}
                maxDate={getDateObject(workorder.targetCompletionDate)}
                onChange={handleTargetStartDateChange}
                showIcon
              />
            ) : (
              workorder?.targetStartDate &&
              t("common.formattedDateShort", {
                dateString: workorder?.targetStartDate,
              })
            )
          }
        />
        <FormRow
          label={t("workorders.targetCompletionDate")}
          value={
            editingWorkorder ? (
              <InternationalDatepicker
                value={getDateObject(workorder.targetCompletionDate)}
                minDate={getDateObject(workorder.targetStartDate)}
                onChange={handleTargetCompletionDateChange}
                showIcon
              />
            ) : (
              workorder?.targetCompletionDate &&
              t("common.formattedDateShort", {
                dateString: workorder?.targetCompletionDate,
              })
            )
          }
        />
        <FormRow label={t("workorders.workorderType")} value={workorder?.workOrderType} />
      </FormContainer>
    </Card>
  );
}
